/**
 * A grading app's Unit (client 2026-11-03): how many players its coach grades
 * on one play — the player rows Play Entry shows, 1 to 6. Set in Settings
 * under Jersey #, and saved on that field (`unit`), so every device of the
 * team gets it; five, as every app showed until then, while it's unset.
 */

export const MAX_UNIT = 6;
export const DEFAULT_UNIT = 5;

/** Every player slot a play can carry: 1…6. */
export const PLAYER_SLOTS: readonly number[] = Array.from({ length: MAX_UNIT }, (_, i) => i + 1);

/** A player slot's fields: Jersey #, grade, comment, Production, Correctables. */
export const slotFieldNames = (n: number): string[] => [
  `jerseyNumber${n}`,
  `grade${n}`,
  `comment${n}`,
  `prod${n}`,
  `corr${n}`,
];

/** Play Entry's player rows for a Unit: each row's fields, and the fields by kind. */
export function playerFieldGroups(unit: number) {
  const slots = PLAYER_SLOTS.filter((n) => n <= unit);
  return {
    gradingRows: slots.map(slotFieldNames),
    dropdownFields: slots.map((n) => `jerseyNumber${n}`),
    gradingFields: slots.map((n) => `grade${n}`),
    commentFields: slots.map((n) => `comment${n}`),
    prodFields: slots.map((n) => `prod${n}`),
    corrFields: slots.map((n) => `corr${n}`),
  };
}

/** The field the Unit is saved on. */
export const UNIT_FIELD = "jerseyNumber1";

export const isUnit = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= MAX_UNIT;

/** The Unit saved in Settings; five when unset (or not 1–6). */
export function unitOf(fields: readonly { name: string; unit?: unknown }[] | null | undefined): number {
  const saved = fields?.find((f) => f?.name === UNIT_FIELD)?.unit;
  return isUnit(saved) ? saved : DEFAULT_UNIT;
}

/**
 * A play as Play Entry enters it: the players past the Unit left out — Play
 * Entry still holds them from an earlier play or a larger Unit (players stay
 * picked from play to play), but they aren't on screen.
 */
export function withinUnit<T extends Record<string, unknown>>(selections: T, unit: number): T {
  const play: Record<string, unknown> = { ...selections };
  for (const n of PLAYER_SLOTS) {
    if (n <= unit) continue;
    for (const name of slotFieldNames(n)) if (name in play) play[name] = "";
  }
  return play as T;
}

// A cell is empty when blank, an emptied multi-select, or the field's own name
// (what an untouched Play Entry field holds).
const filled = (value: unknown, name: string): boolean =>
  Array.isArray(value) ? value.length > 0 : value != null && String(value).trim() !== "" && value !== name;

/**
 * The player columns a list of plays shows: the Unit's, and any past it that
 * a play filled (entered with a larger Unit) — a smaller Unit never hides
 * what was recorded. A grade alone isn't a record: Play Entry sets every row
 * on screen to "+" itself, so the plays saved before the Unit carry one in
 * each of the five slots, player or not.
 */
export function slotsShown(
  plays: readonly (Record<string, unknown> | null | undefined)[] | null | undefined,
  unit: number
): number {
  let shown = unit;
  for (const play of plays ?? []) {
    if (!play) continue;
    for (let n = MAX_UNIT; n > shown; n--) {
      if (slotFieldNames(n).some((name) => name !== `grade${n}` && filled(play[name], name))) {
        shown = n;
        break;
      }
    }
  }
  return shown;
}
